// src/components/CustomerLedgerModal.tsx
//
// One customer's A/R ledger with a running balance (recipe customer-ledger,
// flag analytics.arledger, agent 3.20.0+) — the PBI Kitchen Sink "AR" page.
// Opened from the A/R Detail drilldown. Invoices / credit memos and payments
// in date order from the opening balance; the footer ties the closing
// balance to Σ open invoice balances and shows any difference (unapplied
// cash, write-offs) instead of hiding it.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Download, FileSpreadsheet, Loader2, AlertTriangle, X } from 'lucide-react';
import { apiV2 as api } from '../services/apiV2';
import type { CustomerLedgerEntry } from '../types';
import { exportCsv, type CsvColumns } from '../lib/csvExport';
import { exportXlsx } from '../lib/xlsxExport';
import Modal from './Modal';

const fmt = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
const MONTHS = [3, 6, 12, 24, 36];
// Cent-level differences are payment rounding; only a dollar or more is worth a warning.
const DIFF_WARN = 1;

const TYPE_STYLE: Record<CustomerLedgerEntry['type'], string> = {
  invoice:  'text-slate-300',
  credit:   'text-amber-300',
  payment:  'text-emerald-300',
  reversal: 'text-red-300',
};

const COLS: CsvColumns<CustomerLedgerEntry> = [
  ['Date',      (e) => e.date],
  ['Type',      (e) => e.type],
  ['Reference', (e) => e.reference],
  ['Note',      (e) => e.note],
  ['Debit',     (e) => e.debit || ''],
  ['Credit',    (e) => e.credit || ''],
  ['Balance',   (e) => e.balance],
];

export default function CustomerLedgerModal({ code, name, onClose }: { code: number; name: string; onClose: () => void }) {
  const [months, setMonths] = useState(12);
  const q = useQuery({
    queryKey: ['customerLedger', code, months],
    queryFn: () => api.getCustomerLedger({ code, months }),
    staleTime: 60_000,
    retry: 1,
  });
  const d = q.data;
  const entries = d?.entries ?? [];
  const file = `ledger-${code}`;

  return (
    <Modal onClose={onClose} panelClassName="max-w-4xl" showHeader={false}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpen size={18} className="text-blue-400" />
          <div>
            <h3 className="text-sm font-semibold text-white">A/R ledger — {d?.name || name} (#{code})</h3>
            {d && <p className="text-[11px] text-slate-500">{d.dateFrom} → {d.dateTo}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select value={months} onChange={e => setMonths(Number(e.target.value))}
            className="px-2 py-1 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {MONTHS.map(m => <option key={m} value={m}>{m} months</option>)}
          </select>
          <button onClick={() => exportCsv(file, COLS, entries)} disabled={entries.length === 0} title="Export to CSV"
            className="px-2 py-1 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
            <Download size={11} /> CSV
          </button>
          <button onClick={() => exportXlsx(file, COLS, entries)} disabled={entries.length === 0} title="Export to Excel"
            className="px-2 py-1 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
            <FileSpreadsheet size={11} /> Excel
          </button>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={16} /></button>
        </div>
      </div>

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : q.error ? (
        <div className="p-3 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> Couldn't load the ledger: {(q.error as Error).message}
        </div>
      ) : d && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
            <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/40">
              <div className="text-slate-500">Opening</div><div className="text-white font-medium tabular-nums">{fmt(d.openingBalance)}</div>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/40">
              <div className="text-slate-500">Invoiced</div><div className="text-white font-medium tabular-nums">{fmt(d.debits)}</div>
            </div>
            <div className="p-2 rounded-lg bg-slate-800/50 border border-slate-700/40">
              <div className="text-slate-500">Paid / credited</div><div className="text-emerald-300 font-medium tabular-nums">{fmt(d.credits)}</div>
            </div>
            <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/30">
              <div className="text-slate-400">Closing</div><div className="text-white font-semibold tabular-nums">{fmt(d.closingBalance)}</div>
            </div>
          </div>

          <div className="max-h-[55vh] overflow-y-auto rounded-lg border border-slate-700/50">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-800">
                <tr className="text-slate-400">
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Reference</th>
                  <th className="px-3 py-2 text-left font-medium">Note</th>
                  <th className="px-3 py-2 text-right font-medium">Debit</th>
                  <th className="px-3 py-2 text-right font-medium">Credit</th>
                  <th className="px-3 py-2 text-right font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-800/60 text-slate-500 italic">
                  <td className="px-3 py-1.5">{d.dateFrom}</td>
                  <td className="px-3 py-1.5" colSpan={5}>Opening balance</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmt(d.openingBalance)}</td>
                </tr>
                {entries.map((e, i) => (
                  <tr key={`${e.date}-${e.reference}-${i}`} className="border-b border-slate-800/40 hover:bg-slate-700/20">
                    <td className="px-3 py-1.5 text-slate-300 whitespace-nowrap">{e.date}</td>
                    <td className={`px-3 py-1.5 capitalize ${TYPE_STYLE[e.type]}`}>{e.type}</td>
                    <td className="px-3 py-1.5 text-slate-300">{e.reference}</td>
                    <td className="px-3 py-1.5 text-slate-500">{e.note}</td>
                    <td className="px-3 py-1.5 text-right text-slate-200 tabular-nums">{e.debit ? fmt(e.debit) : ''}</td>
                    <td className="px-3 py-1.5 text-right text-emerald-300 tabular-nums">{e.credit ? fmt(e.credit) : ''}</td>
                    <td className="px-3 py-1.5 text-right text-white tabular-nums">{fmt(e.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entries.length === 0 && <div className="text-center py-6 text-slate-500 text-sm">No activity in this period</div>}
          </div>

          <p className={`mt-2 text-[11px] ${Math.abs(d.difference) >= DIFF_WARN ? 'text-amber-400' : 'text-slate-500'}`}>
            Open invoice balances total {fmt(d.openInvoiceBalance)}
            {Math.abs(d.difference) >= DIFF_WARN
              ? <> — {fmt(Math.abs(d.difference))} {d.difference > 0 ? 'less' : 'more'} than the ledger (unapplied payments, write-offs or adjustments).</>
              : Math.abs(d.difference) >= 0.01
                ? <> — ties to the ledger within {fmt(Math.abs(d.difference))} (rounding).</>
                : <> — ties to the ledger.</>}
            {d.truncated && <> Showing the first {entries.length} entries; shorten the period to see the rest.</>}
          </p>
        </>
      )}
    </Modal>
  );
}
